import { NextRouter } from "next/router"
import { CustomerMessageSubtype } from "@/types/chat"
import { DayObject, DayRange, START_OF_DAY, TimeOfDay } from "@/types/date"
import { EntityRef } from "@/types/entity"
import { FlightSearchInput } from "@/types/flights/flight-search-input"
import { ItineraryEvent } from "@/types/itinerary"
import { Place } from "@/types/place"
import {
  HotelQuote,
  isHotelQuote,
  roomFromHotelQuote,
} from "@/types/price-quote"
import { TravelerGroup, TripRef, TripSummary } from "@/types/trip"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { placeToAmplitudeProperties } from "@/lib/analytics/amplitude-properties"
import { AmplitudeAskAIPlaceType } from "@/lib/analytics/amplitude-types"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { createItineraryActions } from "@/lib/itinerary-ui-actions"
import { isEventWithMindtripHotelBooking } from "@/lib/itinerary-utils"
import { isChatPage } from "@/lib/utils/page"
import {
  beforeDayObject,
  browserToday,
  getTimeOfDay,
  getTimestamp,
  sameDay,
  today,
} from "../date-utils"
import { isAirport, isLocation, isRestaurant } from "../entity-utils"
import {
  defaultTimeRange,
  defaultTimeSpan,
  endDay,
  itineraryEventHasFixedSchedule,
  startDay,
  timeOfDayForEvent,
} from "../itinerary-utils"
import {
  getRememberedPlaceDates,
  getRememberedPlaceTravelers,
} from "../place-search-memory"
import {
  getDraftFallbackDates,
  getTripBasedPlaceSearchDates,
  resolvePlaceDetailSearchParams,
} from "../place-search-params"
import { placeDateKind } from "../place-utils"
import { dayRangeForTimeFrame } from "../time-frame-utils"
import {
  getTripTravelersOrDefault,
  resolveRoomForTravelers,
} from "../trip-utils"
import { priceQuotesApi } from "./api/price-quotes-api"
import { generatePlaceChat } from "./chat-thunks"
import {
  closeDetailSheet,
  openEphemeralPlaceQuestionAnswerSheet,
  selectEntitySheetEntityRef,
  selectTripInSheetStack,
  setPriceQuoteRef,
} from "./detail-sheet-slice"
import { selectIsEmbeddedChatView } from "./embedded-slice"
import { openFlightSheetWithSearchInput } from "./flight-search-slice"
import { selectEventsWithPlaces } from "./itinerary-slice"
import placeSlice, {
  selectHasConfirmedHotelBooking,
  selectItineraryEvent,
  selectPlaceEndDate,
  selectPlaceSearchParams,
  selectPlaceStartDate,
  selectPlaceTravelers,
} from "./place-slice"
import { AppDispatch, RootState } from "./redux-store"
import { selectTripDates, selectTripTravelers } from "./trip-slice"
import userSlice, {
  selectPersistentBotMessages,
  selectSelectedPlaceRoom,
  selectSelectedPlaceSearchParams,
} from "./user-slice"
import { selectVoiceModeOpen, voiceChatActions } from "./voice-chat-slice"

export function placeQuestionAnswerKey(entityRef: EntityRef, question: string) {
  return `place-question:${entityRef}:${question}`
}

export const askPlaceQuestion = createAsyncThunk<
  { openedInChat: boolean },
  {
    place: Place
    question: string
    label: string
    router: NextRouter
    subType?: CustomerMessageSubtype
    amplitudeType: AmplitudeAskAIPlaceType
    range?: DayRange
    travelers?: TravelerGroup
    tripRef?: TripRef
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "place/askPlaceQuestion",
  async (
    {
      place,
      question,
      label,
      router,
      subType,
      amplitudeType,
      range,
      travelers,
      tripRef,
    },
    { dispatch, getState }
  ) => {
    if (selectVoiceModeOpen(getState())) {
      dispatch(voiceChatActions.voiceModeClosed({ reason: "chat_started" }))
    }

    const isEmbeddedChatView = selectIsEmbeddedChatView(getState())
    const isChat = isChatPage(router) || isEmbeddedChatView

    if (isChat) {
      await dispatch(
        generatePlaceChat({
          place,
          question,
          newChat: false,
          subType,
          amplitudeType,
          range,
          travelers,
          tripRef,
        })
      ).unwrap()
      return { openedInChat: true }
    }

    sendAnalyticsEvent({
      name: "AIPlaceAsk",
      properties: {
        ...placeToAmplitudeProperties(place),
        type: amplitudeType,
      },
    })

    await dispatch(
      openEphemeralPlaceQuestionAnswerSheet({
        answerKey: placeQuestionAnswerKey(place.entity_ref, question),
        entityRef: place.entity_ref,
        question,
        label,
      })
    )
    return { openedInChat: false }
  }
)

function parseQuoteDate(dateString: string): DayObject {
  const [year, month, day] = dateString.split("-").map(Number)
  return { year, month, day }
}

function quoteMatchesPlace(quote: HotelQuote, place: Place): boolean {
  return quote.hotel.entity_ref === place.entity_ref
}

function getSearchParamsFromQuote(quote: HotelQuote) {
  return {
    startDate: parseQuoteDate(quote.dates.check_in),
    endDate: parseQuoteDate(quote.dates.check_out),
    travelers: {
      adult_count: quote.people.adults,
      child_count: quote.people.children ?? 0,
      infant_count: 0,
      pet_count: 0,
    },
    room: roomFromHotelQuote(quote),
  }
}

function itineraryEventHasConfirmedHotelBooking(
  event: ItineraryEvent
): boolean {
  return (
    isEventWithMindtripHotelBooking(event) &&
    event.hotel_booking?.status === "confirmed"
  )
}

function resolveDatesFromItineraryEvent(
  event: ItineraryEvent,
  place: Place,
  tripDates: DayRange
): { startDate: DayObject | undefined; endDate: DayObject | undefined } {
  if (
    !tripDates.from &&
    !itineraryEventHasFixedSchedule(event) &&
    !itineraryEventHasConfirmedHotelBooking(event)
  ) {
    return { startDate: undefined, endDate: undefined }
  }

  const eventStart = startDay(event)
  if (event.scheduled === false && !itineraryEventHasFixedSchedule(event)) {
    const tripBasedDates = getTripBasedPlaceSearchDates(place, tripDates)
    return {
      startDate: tripBasedDates.startDate,
      endDate: tripBasedDates.endDate,
    }
  }

  if (beforeDayObject(eventStart, browserToday())) {
    const draftDates = getDraftFallbackDates(place, today(place.time_zone))
    return {
      startDate: draftDates.startDate,
      endDate: draftDates.endDate,
    }
  }

  return { startDate: eventStart, endDate: endDay(event) }
}

export const syncPlaceSearchParamsFromTrip = createAsyncThunk<
  void,
  {
    trip: TripSummary
    place: Place
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "place/syncPlaceSearchParamsFromTrip",
  ({ trip, place }, { dispatch, getState }) => {
    const state = getState()
    if (!selectPlaceSearchParams(state)) return
    if (selectHasConfirmedHotelBooking(state)) return

    const event = selectItineraryEvent(state)
    if (event && itineraryEventHasFixedSchedule(event)) return

    const tripDates = dayRangeForTimeFrame(trip.time_frame)
    const tripBasedDates = getTripBasedPlaceSearchDates(place, tripDates)
    const room = trip.rooms?.[0]
    const travelers = getTripTravelersOrDefault(trip)
    const searchParams = {
      startDate: tripBasedDates.startDate,
      endDate: tripBasedDates.endDate,
      travelers,
      room: resolveRoomForTravelers(travelers, room),
    }

    dispatch(placeSlice.actions.updateSearchParams(searchParams))
    dispatch(
      userSlice.actions.updateSelectedPlaceSearchParams({
        ...searchParams,
        dateKind: placeDateKind(place),
        source: "tripSync",
      })
    )
  }
)

export const initPlaceDetail = createAsyncThunk<
  void,
  {
    place: Place
    itineraryEvent?: ItineraryEvent
    scheduleStartAt?: number
    priceQuoteRef?: string
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "place/initPlaceDetail",
  async (
    { place, itineraryEvent, scheduleStartAt, priceQuoteRef },
    { dispatch, getState }
  ) => {
    const state = getState()

    // Bail out if the entity sheet has already moved to a different entity.
    // This prevents a stale useEffect-triggered call from resetting state
    // (placeSlice.reset) or clearing the quoteRef for the new entity.
    const sheetEntityRef = selectEntitySheetEntityRef(state)
    if (
      sheetEntityRef &&
      state.detailSheetSlice.open &&
      sheetEntityRef !== place.entity_ref
    ) {
      return
    }

    const events = selectEventsWithPlaces(state)
    const tripDates = selectTripDates(state)
    const tripTravelers = selectTripTravelers(state)
    const preference = selectSelectedPlaceSearchParams(state)
    const selectedPlaceRoom = selectSelectedPlaceRoom(state)
    const sheetPlaceSearchParams = state.detailSheetSlice.placeSearchParams

    // When the place was opened from a bot message whose intent carries dates or
    // party size the customer explicitly stated ("dinner for two on June 30th"),
    // prefer those over trip-level values when newer than a session pick.
    const sheetMessageId = state.detailSheetSlice.messageId
    const sheetMessage = sheetMessageId
      ? selectPersistentBotMessages(state).find((m) => m.id === sheetMessageId)
      : undefined
    const messageIntent = sheetMessage?.intent
    const intentStartDate =
      messageIntent?.start_date &&
      !beforeDayObject(messageIntent.start_date, browserToday())
        ? messageIntent.start_date
        : undefined
    const intentTravelers = messageIntent?.travelers

    dispatch(placeSlice.actions.reset())
    dispatch(placeSlice.actions.setScheduleStartAt(scheduleStartAt))

    // If we are not passed an event (i.e. click on the place from the chat message),
    // then try to do our best to match an event with the entity_ref
    // Potential bug here that we are editing the wrong itineraryEvent
    // Currently, we pick the very first one.
    const explicitEvent = itineraryEvent
    const autoMatchedEvent = explicitEvent
      ? undefined
      : events.find((e) => e.place?.entity_ref === place.entity_ref)
    const event = explicitEvent || autoMatchedEvent

    // Set the itinerary event
    dispatch(placeSlice.actions.setItineraryEvent(event))
    dispatch(placeSlice.actions.setHotelBooking(event?.hotel_booking))

    let quoteSearchParams:
      | ReturnType<typeof getSearchParamsFromQuote>
      | undefined = undefined

    if (!event && priceQuoteRef) {
      const cachedQuote = priceQuotesApi.endpoints.getPriceQuote.select({
        ref: priceQuoteRef,
      })(state).data

      if (cachedQuote) {
        if (
          isHotelQuote(cachedQuote) &&
          quoteMatchesPlace(cachedQuote, place)
        ) {
          quoteSearchParams = getSearchParamsFromQuote(cachedQuote)
        } else {
          dispatch(setPriceQuoteRef(undefined))
        }
      } else {
        try {
          const fetchedQuote = await dispatch(
            priceQuotesApi.endpoints.getPriceQuote.initiate({
              ref: priceQuoteRef,
            })
          ).unwrap()

          const latestState = getState()
          if (
            latestState.detailSheetSlice.entity?.entity_ref !==
              place.entity_ref ||
            latestState.detailSheetSlice.priceQuoteRef !== priceQuoteRef
          ) {
            return
          }

          if (
            isHotelQuote(fetchedQuote) &&
            quoteMatchesPlace(fetchedQuote, place)
          ) {
            quoteSearchParams = getSearchParamsFromQuote(fetchedQuote)
          } else {
            dispatch(setPriceQuoteRef(undefined))
          }
        } catch {
          const latestState = getState()
          if (
            latestState.detailSheetSlice.entity?.entity_ref ===
              place.entity_ref &&
            latestState.detailSheetSlice.priceQuoteRef === priceQuoteRef
          ) {
            dispatch(setPriceQuoteRef(undefined))
          } else {
            return
          }
        }
      }
    }

    const dateKind = placeDateKind(place)
    const rememberedDates = getRememberedPlaceDates(dateKind)
    const rememberedTravelers = getRememberedPlaceTravelers()

    const resolved = resolvePlaceDetailSearchParams({
      place,
      dateKind,
      browserToday: browserToday(),
      tripDates,
      tripTravelers: tripTravelers ?? undefined,
      preference,
      rememberedTravelers,
      rememberedDates,
      explicitEventDates: explicitEvent
        ? {
            ...resolveDatesFromItineraryEvent(explicitEvent, place, tripDates),
            event: explicitEvent,
          }
        : undefined,
      confirmedHotelEventDates:
        autoMatchedEvent &&
        !explicitEvent &&
        itineraryEventHasConfirmedHotelBooking(autoMatchedEvent)
          ? {
              ...resolveDatesFromItineraryEvent(
                autoMatchedEvent,
                place,
                tripDates
              ),
              event: autoMatchedEvent,
            }
          : undefined,
      quoteDates: quoteSearchParams
        ? {
            startDate: quoteSearchParams.startDate,
            endDate: quoteSearchParams.endDate,
            travelers: quoteSearchParams.travelers,
          }
        : undefined,
      sheetParams: sheetPlaceSearchParams,
      autoMatchedEventDates:
        autoMatchedEvent &&
        !explicitEvent &&
        !itineraryEventHasConfirmedHotelBooking(autoMatchedEvent)
          ? {
              ...resolveDatesFromItineraryEvent(
                autoMatchedEvent,
                place,
                tripDates
              ),
              event: autoMatchedEvent,
            }
          : undefined,
      intentStartDate,
      intentEndDate: messageIntent?.end_date,
      messageTimestamp: sheetMessage?.timestamp.epoch_millis,
      intentTravelers: intentTravelers ?? undefined,
    })

    dispatch(
      placeSlice.actions.setSearchParams({
        startDate: resolved.startDate,
        endDate: resolved.endDate,
        travelers: resolved.travelers,
        room:
          quoteSearchParams?.room ??
          resolveRoomForTravelers(resolved.travelers, selectedPlaceRoom),
        reservationTime: undefined,
      })
    )
  }
)

export const handleCheckFlights = createAsyncThunk<
  void,
  Place,
  { state: RootState; dispatch: AppDispatch }
>("place/handleCheckFlights", async (place, { dispatch, getState }) => {
  const travelers = selectPlaceTravelers(getState())
  const start = selectPlaceStartDate(getState())
  const end = selectPlaceEndDate(getState())

  if (!travelers) return

  const searchInput: FlightSearchInput = {
    trip_type: "round-trip",
    origin: undefined,
    destination: isAirport(place)
      ? { iata: place.code }
      : isLocation(place)
        ? { entity_ref: place.entity_ref }
        : undefined,
    departure_date: start ?? undefined,
    return_date: end ?? undefined,
    cabin_class: undefined,
    adult_count: travelers.adult_count,
    child_count: travelers.child_count,
    infant_on_lap_count: undefined,
    infant_in_seat_count: undefined,
    selected_options: undefined,
  }
  void dispatch(openFlightSheetWithSearchInput({ searchInput }))
})

export const handleRecommendHotels = createAsyncThunk<
  void,
  { place: Place; router: NextRouter },
  { state: RootState; dispatch: AppDispatch }
>(
  "place/handleRecommendHotels",
  async ({ place, router }, { dispatch, getState }) => {
    const question = `Recommend some hotels in ${place.name}. Group similar hotels and provide pros and cons.`

    try {
      const { openedInChat } = await dispatch(
        askPlaceQuestion({
          place,
          question,
          label: "Recommend hotels",
          router,
          subType: "chat-action-pill",
          amplitudeType: "custom",
        })
      ).unwrap()

      if (openedInChat) {
        void dispatch(closeDetailSheet())
      }
    } catch {
      const state = getState()
      if (!isChatPage(router) && !selectIsEmbeddedChatView(state)) {
        void router.push("/chat/new")
      }
    }
  }
)

export const handleAddToTrip = createAsyncThunk<
  ItineraryEvent | undefined,
  {
    place: Place
    start?: DayObject
    end?: DayObject
    travelers: TravelerGroup
    reservationTime?: TimeOfDay
    updateTripTimeFrame?: boolean
    tripRef: TripRef
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "place/handleAddToTrip",
  async (
    {
      place,
      start,
      end,
      travelers,
      reservationTime,
      updateTripTimeFrame,
      tripRef,
    },
    { dispatch, getState }
  ) => {
    const state = getState()
    const event = selectItineraryEvent(state)

    const itineraryActions = createItineraryActions({
      dispatch,
      tripRef,
      tripSheetOpen: selectTripInSheetStack(state),
    })
    if (start && end) {
      const defaultStartEndTime = defaultTimeRange(place, start, end)
      // If we have an existing itinerary event, keep the original start and end time of that event
      // Otherwise, we default the start and end time based on type
      // TODO: unclear who's responsible for setting good default times,
      // all-day flag, etc., throughout the UI.
      let startTime = event
        ? timeOfDayForEvent(event, "start")
        : defaultStartEndTime.from
          ? getTimeOfDay(defaultStartEndTime.from, place.time_zone)
          : START_OF_DAY
      let endTime = event
        ? timeOfDayForEvent(event, "end")
        : defaultStartEndTime.to
          ? getTimeOfDay(defaultStartEndTime.to, place.time_zone)
          : START_OF_DAY

      if (!event && isRestaurant(place) && reservationTime) {
        startTime = {
          ...reservationTime,
          second: reservationTime.second ?? 0,
          millisecond: reservationTime.millisecond ?? 0,
        }
        endTime = getTimeOfDay(
          defaultTimeSpan(
            "restaurant",
            getTimestamp(start, startTime, place.time_zone)
          ),
          place.time_zone
        )
      }

      const startTimestamp = getTimestamp(start, startTime, place.time_zone)
      const endTimestamp = getTimestamp(end, endTime, place.time_zone)

      const newAllDay =
        (isAirport(place) || isLocation(place)) && !sameDay(start, end)
          ? true
          : undefined

      if (event) {
        return await itineraryActions.changeEvent({
          event,
          newStart: startTimestamp,
          newEnd: endTimestamp,
          newAllDay: newAllDay,
          newTravelers: travelers,
          updateTripTimeFrame,
        })
      } else {
        return await itineraryActions.addPlaceEvent({
          place: place,
          start: startTimestamp,
          end: endTimestamp,
          // The timestamps above already carry the intended times (including
          // the user's reservation time); don't let the builder re-derive
          // defaults from the day alone.
          defaultTime: false,
          allDay: newAllDay,
          travelers: travelers,
          updateTripTimeFrame,
        })
      }
    }
  }
)
